<template>
  <v-navigation-drawer id="app-drawer" v-model="drawer" app dark>
    <v-img height="100%" class="imgBg" gradient="to top, rgba(27,27,27,.74), rgba(27,27,27,.74)" :src="imgSrc">
      <v-list dark>
        <v-list-tile class="mx-3 my-0" avatar>
          <v-list-tile-avatar tile>
            <v-img contain :src="logo" />
          </v-list-tile-avatar>

          <v-list-tile-title class="white--text title font-weight-light">Karta Mieszkańca</v-list-tile-title>
        </v-list-tile>

        <v-divider class="mx-3 mb-2" />

        <span v-for="(item, index) in navbarItems" :key="index">
          <v-list-tile v-if="!item.children" class="mx-4" :to="item.to" :active-class="color">
            <v-list-tile-action>
              <v-icon>{{ item.icon }}</v-icon>
            </v-list-tile-action>

            <v-list-tile-title>{{ item.text }}</v-list-tile-title>
          </v-list-tile>

          <v-list-group v-else sub-group prepend-icon="mdi-tune" :value="false">
            <template v-slot:activator>
              <v-list-tile class="mx-4">
                <v-list-tile-title>{{ item.text }}</v-list-tile-title>
              </v-list-tile>
            </template>

            <v-list-tile
              v-for="(item, index) in item.children"
              class="mx-4"
              :key="index"
              :to="item.to"
              :active-class="color"
            >
              <v-list-tile-action>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-tile-action>

              <v-list-tile-title>{{ item.text }}</v-list-tile-title>
            </v-list-tile>
          </v-list-group>
        </span>
      </v-list>
    </v-img>
  </v-navigation-drawer>
</template>

<script>
import { authResource, authService } from '@witkac/kartamiejska-resources';
import { isPartner, ProfileType, SessionStorage } from '@witkac/kartamiejska-shared';

import { eventBus } from '@/main';
import { mapState } from 'vuex';

export default {
  data() {
    const partnerId = parseInt(localStorage.getItem(SessionStorage.PARTNER_ID));
    const navbarItems = [
      {
        icon: 'mdi-view-dashboard',
        to: { name: 'dashboard' },
        text: 'Start',
        permission: ['STATS.PUBLIC'],
        entityType: 'partner',
        entityId: partnerId
      },
      { icon: 'mdi-account', to: { name: 'profile' }, text: 'Twoje dane' },
      {
        icon: 'mdi-folder-account',
        to: { name: 'partners' },
        text: 'Partnerzy',
        profileType: ProfileType.OFFICIAL,
        permission: ['PARTNER.LIST']
      },
      {
        icon: 'mdi-account-details',
        to: { name: 'clientList' },
        text: 'Mieszkańcy',
        permission: ['PROFILE.LIST'],
        profileType: ProfileType.OFFICIAL
      },
      {
        icon: 'mdi-account-details',
        to: { name: 'officials' },
        text: 'Operatorzy',
        profileType: ProfileType.OFFICIAL,
        permission: ['PROFILE.LIST']
      },
      {
        partner: true,
        icon: 'mdi-folder-account',
        to: { name: 'partner', params: { id: partnerId } },
        text: 'Profil partnera',
        profileType: ProfileType.PARTNER,
        permission: ['PARTNER.GET'],
        entityType: 'partner',
        entityId: partnerId
      },
      {
        icon: 'mdi-account-details',
        to: { name: 'employees' },
        text: 'Pracownicy',
        profileType: ProfileType.PARTNER,
        permission: ['PARTNER.GET'],
        entityType: 'partner',
        entityId: partnerId
      },
      {
        icon: 'mdi-file-document-outline',
        to: '/applications',
        text: 'Wnioski',
        permission: ['APPLICATION.LIST'],
        profileType: ProfileType.OFFICIAL
      },

      {
        icon: 'mdi-cards-outline',
        to: '/benefits',
        text: 'Usługi',
        permission: ['BENEFIT.LIST'],
        profileType: ProfileType.OFFICIAL
      },
      {
        icon: 'mdi-card-bulleted',
        text: 'Karty',
        to: '/cards',
        permission: ['CARD.LIST'],
        profileType: ProfileType.OFFICIAL
      },
      {
        icon: 'mdi-chart-timeline',
        text: 'Użycie kart',
        to: '/benefitUsages',
        permission: ['BENEFIT_USE.LIST'],
        profileType: ProfileType.OFFICIAL
      },
      {
        icon: 'mdi-shopping',
        permission: ['POINT_CONTAINER.LIST'],
        text: 'Kontenery',
        to: '/containers',
        profileType: ProfileType.OFFICIAL
      },
      {
        icon: 'mdi-tune',
        text: 'Konfiguracja',
        to: { name: 'config' },
        permission: ['CONFIG.GET'],
        profileType: ProfileType.OFFICIAL
      }
    ];
    const profileType = localStorage.getItem(SessionStorage.PROFILE_TYPE);
    return {
      drawer: true,
      items: navbarItems.filter(item => !item.profileType || item.profileType === profileType),
      imgSrc: '/img/tlo.jpg',
      logo: '/img/logo.png', // Tymczasowo na sztywno przypisane
      color: 'green darken-3'
    };
  },
  computed: mapState({
    partnerName: state => state.partners.data.name,
    navbarItems(state) {
      const checkedPermissions = state.access.checked;
      const checkPermission = this.checkPermission.bind(this, checkedPermissions);
      const navbarItems = this.items.filter(checkPermission).map(menuItem => ({ ...menuItem }));
      navbarItems
        .filter(item => !!item.children)
        .forEach(item => (item.children = item.children.filter(checkPermission)));

      if (isPartner() && this.partnerName) {
        const partnerItem = navbarItems.find(item => item.partner);
        if (partnerItem) {
          partnerItem.text = this.partnerName;
        }
      }

      return navbarItems;
    }
  }),

  methods: {
    checkPermission(checkedPermissions, menuItem) {
      if (!menuItem.permission) {
        return true;
      }
      if (menuItem.entityType && menuItem.entityId) {
        return menuItem.permission.some(
          permission => checkedPermissions[`${permission}:${menuItem.entityType}:${menuItem.entityId}`]
        );
      }
      return menuItem.permission.some(permission => checkedPermissions[permission]);
    },

    toggleMenu() {
      this.drawer = !this.drawer;
    },

    getRequiredPermissions() {
      const permissions = [];
      const menuItems = [...this.items];
      for (const menuItem of menuItems) {
        if (menuItem.children) {
          menuItems.push(...menuItem.children);
        }
        if (Array.isArray(menuItem.permission)) {
          menuItem.permission.forEach(permission =>
            permissions.push(
              menuItem.entityType && menuItem.entityId
                ? `${permission}:${menuItem.entityType}:${menuItem.entityId}`
                : permission
            )
          );
        }
      }
      return permissions;
    }
  },
  beforeMount() {
    eventBus.$on('toggleMenu', () => this.toggleMenu());
    this.$store.dispatch('access/check', this.getRequiredPermissions());
  }
};
</script>

<style>
#app-drawer .v-responsive__content {
  width: 100% !important;
}
</style>
